import type {
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ForwardedRef,
  ReactNode,
} from 'react';
import { forwardRef } from 'react';

import classNames from 'classnames';

import styles from './RadioButton.module.css';

import { useElementState } from '../../hooks/useElementState';
import { visuallyHidden } from '../../styles';
import { Text } from '../Text';

export interface RadioButtonProps extends ComponentPropsWithoutRef<'input'> {
  label?: ReactNode;
}

/**
 * Render a styled radio input button.
 *
 * @param props - The {@link RadioButtonProps} to render the input field.
 * @see https://corgi-x.tfd.engineering/components/radiobutton
 */
export const RadioButton = forwardRef(RadioButton_);

function RadioButton_(
  props: RadioButtonProps,
  ref?: ForwardedRef<HTMLInputElement>
) {
  const { label = '', className, ...radioButtonProps } = props;

  const [state, handlers] = useElementState({
    autoFocus: props.autoFocus,
    disabled: props.disabled,
  });

  const handleFocus: FocusEventHandler<HTMLInputElement> = e => {
    handlers.onFocus(e);
    props.onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    handlers.onBlur(e);
    props.onBlur?.(e);
  };

  return (
    <label className={classNames(styles.container, className)}>
      <input
        type="radio"
        ref={ref}
        checked={radioButtonProps.checked}
        className={classNames(visuallyHidden)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...radioButtonProps}
      />
      <span
        aria-hidden
        className={classNames(styles.radio, styles[state], {
          [styles.checked]: radioButtonProps.checked,
        })}
      />

      <Text
        className={classNames(styles.label, styles[state], {
          [styles.checked]: radioButtonProps.checked,
        })}
      >
        {label}
      </Text>
    </label>
  );
}
